import { useRef, useState, type DragEvent } from "react";
import { UploadCloud, File, X, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { formatBytes } from "@/lib/format";
import { useT } from "@/i18n";

export type PickedFile = { name: string; sizeBytes: number; file: File };

type Props = {
  label?: string;
  hint?: string;
  value?: PickedFile | null;
  onChange?: (file: PickedFile | null) => void;
  accept?: string;
  className?: string;
  compact?: boolean;
  optional?: boolean;
};

export function UploadDropzone({
  label,
  hint,
  value,
  onChange,
  accept = ".pdf,.jpg,.jpeg,.png",
  className,
  compact,
  optional,
}: Props) {
  const { t } = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOver, setIsOver] = useState(false);

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const f = files[0]!;
    onChange?.({ name: f.name, sizeBytes: f.size, file: f });
  };

  const onDrop = (e: DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setIsOver(false);
    handleFiles(e.dataTransfer.files);
  };

  if (value) {
    return (
      <div className={cn("card flex items-center gap-4 p-4", className)}>
        <span className="inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
          <CheckCircle2 className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          {label && <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>}
          <p className="truncate text-sm font-medium text-slate-900">{value.name}</p>
          <p className="text-xs text-slate-500">{formatBytes(value.sizeBytes)}</p>
        </div>
        <button
          type="button"
          onClick={() => onChange?.(null)}
          className="btn-ghost h-9 w-9 p-0"
          aria-label={t("common.close")}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setIsOver(true);
      }}
      onDragLeave={() => setIsOver(false)}
      onDrop={onDrop}
      className={cn(
        "group relative flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed p-6 text-center transition",
        isOver ? "border-brand-500 bg-brand-50/60" : "border-slate-300 bg-white hover:border-brand-300 hover:bg-slate-50/70",
        compact && "p-4",
        className
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(e) => handleFiles(e.target.files)}
      />
      {label && (
        <div className="w-full text-left">
          <p className="flex items-center gap-2 text-sm font-medium text-slate-900">
            {label}
            {optional && (
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                {t("common.optional")}
              </span>
            )}
          </p>
          {hint && <p className="text-xs text-slate-500">{hint}</p>}
        </div>
      )}
      <div className="flex flex-col items-center gap-2">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-brand-50 text-brand-700">
          {compact ? <File className="h-5 w-5" /> : <UploadCloud className="h-5 w-5" />}
        </span>
        {!compact && (
          <>
            <p className="text-sm font-medium text-slate-900">{t("docs.drop_title")}</p>
            <p className="text-xs text-slate-500">{t("docs.drop_subtitle")}</p>
            <p className="text-xs text-slate-400">{t("docs.drop_formats")}</p>
          </>
        )}
      </div>
    </label>
  );
}
